import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Sparkles,
  Home,
  Grid3X3,
  LayoutDashboard,
  Menu,
  X,
  User,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Settings,
  Crown,
  ChevronDown,
  Key,
  Mail,
  AlertCircle,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import UserProfileModal from './UserProfileModal';
import BoltBadge from './BoltBadge';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const { currentPlan, planLimits } = useSubscriptionLimits();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + N for new room
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && user?.role === 'ADMIN') {
        e.preventDefault();
      }
      // Ctrl/Cmd + D for custom dashboard
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        navigate('/custom-dashboard');
      }
      // Ctrl/Cmd + K for search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        document.getElementById('search-input')?.focus();
      }
      // Escape to close modals
      if (e.key === 'Escape') {
        setShowUserProfile(false);
        setIsMobileMenuOpen(false);
        setShowProfileMenu(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user, navigate]);

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="w-4 h-4" />;
      case 'dark':
        return <Moon className="w-4 h-4" />;
      case 'system':
        return <Monitor className="w-4 h-4" />;
    }
  };

  const cycleTheme = () => {
    const themes: Array<'light' | 'dark' | 'system'> = [
      'light',
      'dark',
      'system',
    ];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowProfileMenu(false);
  };

  const generateUserAvatar = () => {
    if (user?.avatar) {
      return (
        <img
          src={user.avatar}
          alt={user.name}
          className="w-8 h-8 rounded-full object-cover border-2 border-white dark:border-slate-700 shadow-sm"
        />
      );
    }

    // Generate initials avatar
    const initials =
      user?.name
        ?.split(' ')
        .map((n) => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'U';

    const colors = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-pink-500',
      'bg-teal-500',
    ];

    const colorIndex = user?.name ? user.name.charCodeAt(0) % colors.length : 0;
    const bgColor = colors[colorIndex];

    return (
      <div
        className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center text-white text-sm font-semibold border-2 border-white dark:border-slate-700 shadow-sm`}
      >
        {initials}
      </div>
    );
  };

  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'Free';
      case 'pro':
        return 'Pro';
      case 'enterprise':
        return 'Enterprise';
      default:
        return 'Free';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'from-slate-500 to-slate-600';
      case 'pro':
        return 'from-blue-500 to-purple-600';
      case 'enterprise':
        return 'from-purple-600 to-indigo-700';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  const navigationItems = [
    { path: '/', label: 'Home', icon: <Home className="w-5 h-5" /> },
    {
      path: '/rooms',
      label: 'Rooms',
      icon: <Grid3X3 className="w-5 h-5" />,
      requiresAuth: true,
    },
    {
      path: '/dashboards',
      label: 'Dashboards',
      icon: <LayoutDashboard className="w-5 h-5" />,
      requiresAuth: true,
    },
    {
      path: '/api-keys',
      label: 'API',
      icon: <Key className="w-5 h-5" />,
      requiresAuth: true,
    },
  ];

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl shadow-lg border-b border-white/20 dark:border-slate-700/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="relative">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center shadow-lg animate-float">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white dark:border-slate-800 animate-pulse"></div>
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent">
                RoomState
              </h1>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => {
              if (item.requiresAuth && !isAuthenticated) return null;

              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`group flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 font-medium ${
                    isActivePath(item.path)
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }`}
                >
                  <span className={`transition-all duration-300 ${
                    isActivePath(item.path) 
                      ? 'text-white' 
                      : 'text-slate-600 dark:text-slate-400'
                  } ${
                    item.path === '/'
                      ? 'group-hover:rotate-12' // Home icon rotates slightly
                      : item.path === '/rooms'
                      ? 'group-hover:scale-110 group-hover:-translate-y-0.5' // Rooms icon scales and lifts
                      : 'group-hover:rotate-180' // Dashboard icon spins
                  }`}>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-2">
            {/* Bolt.new Badge */}
            <BoltBadge size="small" className="mr-2" />
            
            {/* Plan Indicator - Only show when authenticated */}
            {isAuthenticated && planLimits && (
              <div className={`flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r ${getPlanColor(currentPlan)} text-white rounded-full text-xs font-medium shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer`}
                   onClick={() => setShowUserProfile(true)}
                   title="View subscription details">
                <span>{getPlanDisplayName(currentPlan)}</span>
              </div>
            )}

            <button
              onClick={cycleTheme}
              className="p-2.5 bg-white/70 dark:bg-slate-700/70 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-white/90 dark:hover:bg-slate-700/90 transition-all duration-200 shadow-md hover:shadow-lg border border-slate-200/50 dark:border-slate-600/50 hover:scale-105"
              title={`Theme: ${theme}`}
            >
              {getThemeIcon()}
            </button>

            {isAuthenticated ? (
              <div className="flex items-center space-x-2 pl-2 border-l border-slate-200 dark:border-slate-600">
                {/* Profile Menu */}
                <div className="relative" ref={profileMenuRef}>
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="group flex items-center gap-2 p-2 bg-white/70 dark:bg-slate-700/70 text-slate-700 dark:text-slate-300 rounded-xl hover:bg-white/90 dark:hover:bg-slate-700/90 transition-all duration-200 shadow-md hover:shadow-lg border border-slate-200/50 dark:border-slate-600/50"
                    title="Profile Menu"
                  >
                    {generateUserAvatar()}
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${
                        showProfileMenu ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {/* Profile Dropdown Menu */}
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200/50 dark:border-slate-700/50 py-2 z-50 animate-fade-in-down">
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                          {generateUserAvatar()}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                              {user?.name}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                              {user?.email}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {planLimits && (
                              <div className={`flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r ${getPlanColor(currentPlan)} text-white rounded-full text-xs font-medium`}>
                                <span>{getPlanDisplayName(currentPlan)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Email verification status */}
                        {user && !user.emailConfirmed && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 p-2 rounded-lg">
                            <AlertCircle className="w-3 h-3 flex-shrink-0" />
                            <span>Email not verified</span>
                          </div>
                        )}
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        <button
                          onClick={() => {
                            setShowUserProfile(true);
                            setShowProfileMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                          <User className="w-4 h-4" />
                          <span>Profile Settings</span>
                        </button>

                        <button
                          onClick={() => {
                            navigate('/rooms');
                            setShowProfileMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                          <Grid3X3 className="w-4 h-4" />
                          <span>My Rooms</span>
                        </button>

                        <button
                          onClick={() => {
                            navigate('/dashboards');
                            setShowProfileMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          <span>Dashboards</span>
                        </button>

                        <div className="border-t border-slate-200 dark:border-slate-700 my-2"></div>

                        <button
                          onClick={cycleTheme}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        >
                          {getThemeIcon()}
                          <span>
                            Theme:{' '}
                            {theme.charAt(0).toUpperCase() + theme.slice(1)}
                          </span>
                        </button>

                        <div className="border-t border-slate-200 dark:border-slate-700 my-2"></div>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-3 pl-2 border-l border-slate-200 dark:border-slate-600">
                <button
                  onClick={() => navigate('/login')}
                  className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors font-medium"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center md:hidden">
            {/* Bolt.new Badge - Mobile */}
            <BoltBadge size="small" className="mr-2" />
            
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-xl bg-white/70 dark:bg-slate-700/70 text-slate-700 dark:text-slate-300 hover:bg-white/90 dark:hover:bg-slate-700/90 transition-all duration-200 shadow-md"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200/50 dark:border-slate-700/50 animate-fade-in-down">
            <div className="space-y-2">
              {/* Navigation Items */}
              {navigationItems.map((item) => {
                if (item.requiresAuth && !isAuthenticated) return null;

                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                      isActivePath(item.path)
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                        : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                );
              })}

              {/* Mobile Actions */}
              <div className="pt-4 border-t border-slate-200/50 dark:border-slate-700/50 space-y-2">
                <button
                  onClick={cycleTheme}
                  className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors font-medium"
                >
                  {getThemeIcon()}
                  <span>Theme: {theme}</span>
                </button>

                {isAuthenticated ? (
                  <>
                    <div className="flex items-center gap-3 px-4 py-3 bg-slate-100 dark:bg-slate-700 rounded-xl">
                      {generateUserAvatar()}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-700 dark:text-slate-300 truncate">
                          {user?.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {user?.email}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {planLimits && (
                          <div className={`flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r ${getPlanColor(currentPlan)} text-white rounded-full text-xs font-medium`}>
                            <span>{getPlanDisplayName(currentPlan)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Email verification warning */}
                    {user && !user.emailConfirmed && (
                      <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-700">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-orange-700 dark:text-orange-300 font-medium">Email not verified</p>
                            <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">Please check your inbox and verify your email address</p>
                            <button
                              onClick={() => {
                                setShowUserProfile(true);
                                setIsMobileMenuOpen(false);
                              }}
                              className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              Resend verification email
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <button
                      onClick={() => {
                        setShowUserProfile(true);
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors font-medium"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors font-medium"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Sign Out</span>
                    </button>
                  </>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        navigate('/login');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors font-medium"
                    >
                      <User className="w-5 h-5" />
                      <span>Sign In</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/register');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg font-medium"
                    >
                      <Sparkles className="w-5 h-5" />
                      <span>Get Started</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <UserProfileModal
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
      />
    </nav>
  );
};

export default Navigation;